import { useState } from "react";
import { aprilSchedule } from "@/mocks/calendar";
import { timetables, subjectColors, type ClassKey, type DayKey, type PeriodKey } from "@/mocks/timetable";
import JuicyButton from "@/components/base/JuicyButton";

const CLASSES: ClassKey[] = ["2組", "3組", "4組", "5組", "6組"];

const DAY_COLOR: Record<string, string> = {
  月: "text-black",
  火: "text-black",
  水: "text-sky-600",
  木: "text-black",
  金: "text-red-500",
  土: "text-sky-500",
  日: "text-red-500",
};

// 行事・特別授業として直書きされるキーワード（時間割データから解決しない）
const EVENT_KEYWORDS = [
  "着任式","始業式","対面式","全校集会","学活","道徳","総合","学活",
  "身体測定","視力","鼻喉","防災訓練","地区別集会","遠泳","国語","数学",
  "英語","理科","社会","音楽","美術","保体","技・家","AET","入学式",
];

function isEventSlot(slot: string): boolean {
  if (!slot || slot === "-") return false;
  const dayKeys = ["A","B","C","D","E"];
  // A1〜E6 形式でなければ行事扱い
  if (slot.length >= 2 && dayKeys.includes(slot[0]) && !isNaN(Number(slot[1]))) return false;
  return true;
}

// スロットコード（例: "A3"）から授業名を取得
function resolveSubject(slot: string, cls: ClassKey): string {
  if (!slot || slot === "-") return "-";
  const dayKeys = ["A","B","C","D","E"];
  if (slot.length >= 2 && dayKeys.includes(slot[0]) && !isNaN(Number(slot[1]))) {
    const day = slot[0] as DayKey;
    const period = Number(slot[1]) as PeriodKey;
    return timetables[cls]?.[day]?.[period] ?? "-";
  }
  // 直書きテキスト（行事名・学活・道徳など）はそのまま返す
  return slot;
}

function getColorClass(subject: string, isEvent: boolean): string {
  if (subject === "-" || !subject) return "bg-white text-gray-300 border-gray-100";
  if (isEvent) return "bg-indigo-50 text-indigo-700 border-indigo-200";
  return subjectColors[subject] ?? "bg-gray-50 text-gray-600 border-gray-200";
}

function getTodayStr(): string {
  const now = new Date();
  return `${now.getMonth() + 1}/${now.getDate()}`;
}

export default function CalendarSection() {
  const [selectedClass, setSelectedClass] = useState<ClassKey>("6組");
  const [selectedDate, setSelectedDate] = useState<string | null>(getTodayStr());

  const todayStr = getTodayStr();
  const selectedDay = aprilSchedule.find((d) => d.date === selectedDate);

  return (
    <section id="calendar" className="py-24 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 mb-3">月間カレンダー</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <h2 className="text-3xl md:text-4xl font-black text-black leading-tight">
            4月の時間割
          </h2>
          <div className="flex gap-2 flex-wrap">
            {CLASSES.map((cls) => (
              <JuicyButton
                key={cls}
                onClick={() => setSelectedClass(cls)}
                particleColor={selectedClass === cls ? "#ffffff" : "#000000"}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                  selectedClass === cls
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }`}
              >
                {cls}
              </JuicyButton>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Calendar list */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-bold text-black">2025年4月</p>
              </div>
              <div className="p-3 flex flex-col gap-1">
                {aprilSchedule.map((day) => {
                  const isWeekend = day.dayOfWeek === "土" || day.dayOfWeek === "日";
                  const isHoliday = day.isHoliday;
                  const isToday = day.date === todayStr;
                  const isSelected = day.date === selectedDate;
                  const hasClass = !isHoliday && day.slots.length > 0;

                  if (isWeekend && isHoliday && !day.note) return null;

                  return (
                    <JuicyButton
                      key={day.date}
                      onClick={() => hasClass ? setSelectedDate(day.date) : undefined}
                      disabled={!hasClass}
                      particleColor={isSelected ? "#ffffff" : "#000000"}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors duration-150 ${
                        isSelected
                          ? "bg-black text-white"
                          : hasClass
                          ? "hover:bg-gray-50 cursor-pointer"
                          : "opacity-40 cursor-default"
                      }`}
                    >
                      <div className="flex items-center gap-2 w-16 flex-shrink-0">
                        <span className={`text-sm font-bold ${isSelected ? "text-white" : DAY_COLOR[day.dayOfWeek]}`}>
                          {day.date.split("/")[1]}
                        </span>
                        <span className={`text-xs ${isSelected ? "text-white/60" : "text-gray-400"}`}>
                          {day.dayOfWeek}
                        </span>
                        {isToday && !isSelected && (
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>
                        )}
                      </div>

                      <div className="flex gap-1 flex-wrap flex-1">
                        {isHoliday ? (
                          <span className={`text-xs ${isSelected ? "text-white/60" : "text-gray-400"}`}>
                            {day.note ?? "休日"}
                          </span>
                        ) : (
                          day.slots.slice(0, 6).map((slot, i) => {
                            const subject = resolveSubject(slot, selectedClass);
                            const isBlank = subject === "-" || !subject;
                            const isEv = isEventSlot(slot);
                            return (
                              <span
                                key={i}
                                className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                  isSelected
                                    ? isBlank ? "text-white/20" : isEv ? "bg-indigo-400/30 text-white" : "bg-white/20 text-white"
                                    : isBlank ? "text-gray-200" : isEv ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {isBlank ? "—" : subject}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </JuicyButton>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              {selectedDay && !selectedDay.isHoliday ? (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">2025年 {selectedDay.date} ({selectedDay.dayOfWeek})</p>
                      <p className="text-lg font-black text-black">{selectedClass}の時間割</p>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col gap-2">
                    {selectedDay.slots.map((slot, i) => {
                      const period = i + 1;
                      const isEv = isEventSlot(slot);
                      const subject = resolveSubject(slot, selectedClass);
                      const isBlank = subject === "-" || !subject;
                      const colorClass = getColorClass(subject, isEv);

                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-4 px-5 py-3.5 rounded-xl border ${colorClass}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-black/5 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold opacity-50">{period}</span>
                          </div>
                          <span className={`font-semibold text-sm flex items-center gap-2 ${isBlank ? "opacity-30" : ""}`}>
                            {isBlank ? "授業なし" : subject}
                            {isEv && !isBlank && (
                              <span className="text-xs font-normal opacity-60 bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">行事</span>
                            )}
                          </span>
                          <span className="ml-auto text-xs opacity-40">{period}時限目</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-4 px-5 py-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 mt-1">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-restaurant-line text-gray-400 text-xs"></i>
                      </div>
                      <span className="text-gray-400 text-sm font-medium">給食（4・5時限目の間）</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center justify-center text-center min-h-64">
                  {selectedDay?.isHoliday ? (
                    <>
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <i className="ri-rest-time-line text-gray-400 text-xl"></i>
                      </div>
                      <p className="text-black font-bold mb-1">{selectedDay.note ?? "休日"}</p>
                      <p className="text-gray-400 text-sm">授業はありません</p>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <i className="ri-calendar-line text-gray-400 text-xl"></i>
                      </div>
                      <p className="text-gray-500 text-sm">日付を選んでください</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
